import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Calendar, Building2 } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/site/motion";
import { PROJECTS, SERVICES, IMAGES } from "@/data/content";
import Seo from "@/components/site/Seo";
import CoverageMap from "@/components/site/CoverageMap";

export default function Progetti() {
    const { t, lang } = useLang();
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);

    const cats = ["all", ...Array.from(new Set(PROJECTS.map((p) => p.category)))];
    const catLabel = (c) => c === "all" ? t.portfolio.all : (SERVICES.find((s) => s.slug === c)?.[lang].name || c);
    const list = filter === "all" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

    React.useEffect(() => {
        if (!active) return;
        const onKey = (e) => { if (e.key === "Escape") setActive(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [active]);

    return (
        <>
            <Seo title={t.seo.projects.t} description={t.seo.projects.d} image={IMAGES.cta} path="/progetti" />
            <section className="relative pt-40 pb-24 bg-cb-dark cb-grid overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src={IMAGES.cta} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-cb-darker/80" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6">
                    <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">04 — Portfolio</Reveal>
                    <Reveal as="h1" delay={0.4} className="font-display text-5xl md:text-6xl font-bold tracking-tighter text-white">{t.projectsPage.title}</Reveal>
                    <Reveal delay={0.8} className="mt-6 max-w-2xl text-slate-300 text-lg">{t.projectsPage.sub}</Reveal>
                </div>
            </section>

            <CoverageMap />

            <section className="py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-wrap gap-3 mb-12">
                        {cats.map((c) => (
                            <button key={c} data-testid={`filter-${c}`} onClick={() => setFilter(c)}
                                className={`px-5 py-2.5 text-sm font-medium border transition-colors ${
                                    filter === c ? "bg-cb-dark text-white border-cb-dark" : "border-slate-300 text-slate-600 hover:border-cb-accent hover:text-cb-accent"
                                }`}>
                                {catLabel(c)}
                            </button>
                        ))}
                    </div>

                    <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <AnimatePresence mode="popLayout">
                            {list.map((p) => (
                                <motion.button layout key={p.id} data-testid={`project-${p.id}`}
                                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                                    onClick={() => setActive(p)}
                                    className="group relative overflow-hidden text-left h-80">
                                    <img src={p.image} alt={p[lang].name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-cb-darker via-cb-darker/30 to-transparent opacity-90" />
                                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                                        <span className="text-cb-accent text-xs uppercase tracking-widest">{catLabel(p.category)} · {p.year}</span>
                                        <h3 className="font-display text-xl font-semibold text-white mt-1">{p[lang].name}</h3>
                                        <span className="text-sm text-slate-300 mt-1">{p[lang].location}</span>
                                    </div>
                                </motion.button>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                </div>
            </section>

            <AnimatePresence>
                {active && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setActive(null)} data-testid="project-lightbox" role="dialog" aria-modal="true" aria-label={active[lang].name}
                        className="fixed inset-0 z-[80] bg-cb-darker/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10">
                        <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()} className="relative bg-white max-w-4xl w-full max-h-[88vh] overflow-y-auto">
                            <button data-testid="lightbox-close" onClick={() => setActive(null)}
                                className="absolute top-4 right-4 z-10 bg-white/90 p-2 text-cb-dark hover:bg-cb-accent hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                            <img src={active.image} alt={active[lang].name} className="w-full h-72 md:h-96 object-cover" />
                            <div className="p-8 md:p-10">
                                <span className="text-cb-accent text-xs uppercase tracking-widest">{catLabel(active.category)}</span>
                                <h2 className="font-display text-3xl font-bold tracking-tight text-cb-dark mt-2">{active[lang].name}</h2>
                                <p className="mt-4 text-slate-600 leading-relaxed">{active[lang].desc}</p>

                                <div className="mt-8 grid sm:grid-cols-3 gap-6 border-t border-slate-200 pt-6">
                                    <div>
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400"><Calendar size={14} />{t.projectsPage.year}</div>
                                        <div className="mt-1 font-display text-lg font-semibold text-cb-dark">{active.year}</div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400"><Building2 size={14} />{t.projectsPage.client}</div>
                                        <div className="mt-1 font-display text-lg font-semibold text-cb-dark">{active.client}</div>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400"><MapPin size={14} />Location</div>
                                        <div className="mt-1 font-display text-lg font-semibold text-cb-dark">{active[lang].location}</div>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">{t.projectsPage.tech}</div>
                                    <div className="flex flex-wrap gap-2">
                                        {active[lang].tech.map((tech, i) => (
                                            <span key={i} className="bg-cb-light border border-slate-200 px-3 py-1.5 text-sm text-cb-dark">{tech}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
